import _classnames from 'clsx';
import PropTypes from 'prop-types';
import React, { forwardRef, memo } from 'react';
import ComplexIcon from '../../icons/ComplexIcon/ComplexIcon';
import Icon from '../../icons/Icon/Icon';
import Tooltip from '../../tooltip/tooltip';

function InputGroup({
  classes,
  disabled,
  errorText,
  iconLeft,
  iconLeftColor,
  iconRight,
  iconRightColor,
  iconRightOnClick,
  iconRightText,
  iconRightTextColor,
  id,
  innerRef,
  inputProps,
  isLoading,
  name,
  onChange,
  placeholder,
  round,
  status,
  type,
  value,
}) {
  const hasIconLeft = !!iconLeft;
  const hasIconRight = !!iconRight || !!status;

  const classesToUse = _classnames('InputGroup', classes, {
    'InputGroup--has-icon-left': hasIconLeft,
    'InputGroup--has-icon-right': hasIconRight,
  });

  const inputClasses = _classnames('TextInput', 'TextInput--full-width', inputProps.className, {
    'TextInput--has-icon-left': hasIconLeft,
    'TextInput--has-icon-right': hasIconRight,
    'TextInput--round': round,
    [`TextInput--${status}`]: !!status,
  });

  const inputPropsToUse = {
    className: inputClasses,
    disabled,
    id,
    name,
    onChange,
    placeholder,
    type,
    value,
    ref: innerRef,
    ...inputProps,
  };
  const hasErrorText = !!errorText;

  let iconRightName = iconRight;
  let iconRightStatusColor = iconRightColor || '#040815';
  if (status === 'warning') {
    iconRightName = 'Warning';
    iconRightStatusColor = '#A80000';
  } else if (status === 'success') {
    iconRightName = 'Circle--check';
    iconRightStatusColor = '#0A9959';
  }
  const iconRightIcon = iconRightName && (
    <Icon
      classes={_classnames({ 'InputGroup__right-icon': !hasErrorText })}
      name={iconRightName}
      size="minor"
      svgProps={{ style: { fill: iconRightStatusColor } }}
    />
  );
  let iconRightElement;
  if (isLoading) {
    iconRightElement = <ComplexIcon classes="InputGroup__right-icon" name="spinner" />;
  } else if (!isLoading && iconRightIcon && iconRightOnClick) {
    iconRightElement = (
      <button type="button" className="InputGroup__right-button" onClick={iconRightOnClick}>
        {iconRightText && (
          <span
            className="InputGroup__right-button__text"
            style={iconRightTextColor ? { color: iconRightTextColor } : null}
          >
            {iconRightText}
          </span>
        )}
        {iconRightIcon}
      </button>
    );
  } else if (!isLoading && iconRightIcon && !iconRightOnClick) {
    iconRightElement = iconRightIcon;
  }

  return (
    <div className={classesToUse}>
      {hasIconLeft && (
        <Icon
          classes="InputGroup__left-icon"
          name={iconLeft}
          size="minor"
          svgProps={{ style: { fill: iconLeftColor || '#040815' } }}
        />
      )}
      <input {...inputPropsToUse} />
      {hasErrorText ? (
        <div className="InputGroup__icon-tooltip">
          <Tooltip block content={errorText} isOpen light small>
            {iconRightElement}
          </Tooltip>
        </div>
      ) : (
        iconRightElement
      )}
    </div>
  );
}

InputGroup.propTypes = {
  classes: PropTypes.string,
  disabled: PropTypes.bool,
  errorText: PropTypes.string,
  iconLeft: PropTypes.string,
  iconLeftColor: PropTypes.string,
  iconRight: PropTypes.string,
  iconRightColor: PropTypes.string,
  iconRightText: PropTypes.string,
  iconRightTextColor: PropTypes.string,
  iconRightOnClick: PropTypes.func,
  id: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  inputProps: PropTypes.object,
  isLoading: PropTypes.bool,
  name: PropTypes.string,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  round: PropTypes.bool,
  status: PropTypes.oneOf(['success', 'warning']),
  type: PropTypes.string,
  value: PropTypes.string,
  /**
   * Inner Ref
   */
  innerRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
};

InputGroup.defaultProps = {
  classes: '',
  disabled: false,
  iconLeft: null,
  iconRight: null,
  iconRightText: undefined,
  iconRightTextColor: undefined,
  id: null,
  inputProps: {},
  isLoading: false,
  name: null,
  onChange: () => {},
  placeholder: '',
  round: false,
  type: 'text',
  value: null,
  status: undefined,
  iconRightColor: undefined,
  iconRightOnClick: undefined,
  iconLeftColor: undefined,
  errorText: undefined,
  innerRef: undefined,
};

/**
 * @type {React.ComponentType<any>}
 */
export default memo(forwardRef((props, ref) => <InputGroup innerRef={ref} {...props} />));
